import PropTypes from "prop-types"
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Button, Flex, Spin } from "antd"
import { toast } from "react-toastify"
import { spacings } from "themes/variables"
import UploadFile from "./UploadFile"
import ImportReportModal from "../ImportReport"
import ImportErrorMessage from "./ImportErrorMessage"
import useClientImport from "./useClientImport"
import { toaster } from "components/common/Toast"
import { Modal } from "ui/Modal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"

/**
 * ImportClientList Component
 * Handles bulk client import functionality
 * Refactored to follow SOLID principles and performance best practices
 */
const ImportClientList = ({ open, onClose, onReuploadId, setOpenImportClientList }) => {
  const { t } = useTranslation()
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const toastIdRef = useRef(null)

  /**
   * Handle successful import
   * Memoized to prevent unnecessary re-creation
   */
  const handleImportSuccess = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Handle import error
   * Memoized to prevent unnecessary re-creation
   */
  const handleImportError = useCallback(
    (error) => {
      setIsReportModalOpen(true)
      const errorMessage = error?.data?.import_message

      if (!errorMessage) return

      const handleViewDetails = () => {
        // Dismiss the toast when user clicks "see more"
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current)
        }
      }

      const errorContent = <ImportErrorMessage errorMessage={errorMessage} onViewDetails={handleViewDetails} />

      // Store the toast ID so we can dismiss it later
      toastIdRef.current = toaster.error(errorContent, { autoClose: false })
    },
    []
  )

  // Use custom hook for import logic
  const { fileData, isLoading, handleFileChange, handleImport, clearFile } = useClientImport({
    onSuccess: handleImportSuccess,
    onError: handleImportError,
    reuploadId: onReuploadId,
    setOpenImportClientList
  })

  // Clear file when modal opens
  useEffect(() => {
    if (open) {
      clearFile()
    }
  }, [open, clearFile])

  /**
   * Handle modal close
   * Memoized to prevent unnecessary re-creation
   */
  const handleModalClose = useCallback(() => {
    if (fileData) {
      setShowUnsavedModal(true)
      return
    }

    onClose()
  }, [fileData, onClose])

  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedModal(false)
    clearFile()
    onClose()
  }, [clearFile, onClose])

  /**
   * Handle report modal close
   * Memoized to prevent unnecessary re-creation
   */
  const handleReportModalClose = useCallback(() => {
    setIsReportModalOpen(false)
    onClose()
  }, [onClose])

  /**
   * Memoized template download URL
   */
  const templateUrl = useMemo(() => `${process.env.REACT_APP_API_URL}/api/v1/guide/client_imports/download_template`, [])

  return (
    <>
      <Modal title={t("clients.import_client_list")} open={open} onCancel={handleModalClose} width="626px" centered maskClosable={false}>
        <Spin spinning={isLoading}>
          <div className="mb-m">
            {t("clients.upload_your_list_in_the_format_template_provided")}{" "}
            <a href={templateUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              {t("clients.download_it_here")}
            </a>
          </div>

          <UploadFile
            key={open ? "open" : "closed"}
            handleFileChangeImport={handleFileChange}
            isReportModalOpen={isReportModalOpen}
          />

          <Flex wrap className="mt-xxl" gap={spacings.S}>
            <Button className="pr-xl pl-xl" color="primary" variant="outlined" size="large" onClick={handleModalClose}>
              {t("global.cancel")}
            </Button>
            <Button className="pr-xl pl-xl" color="primary" variant="solid" size="large" disabled={!fileData} onClick={handleImport}>
              {t("clients.import")}
            </Button>
          </Flex>
        </Spin>
      </Modal>

      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscardChanges} onKeepEditing={() => setShowUnsavedModal(false)} />

      {isReportModalOpen && <ImportReportModal isModalOpen={isReportModalOpen} onClose={handleReportModalClose} />}
    </>
  )
}
export default ImportClientList

ImportClientList.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReuploadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setOpenImportClientList: PropTypes.func.isRequired
}
